/* eslint-disable no-console */
import { useEffect } from 'react';
import type * as Ably from 'ably';
import { useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { useRFQStore } from 'pages/auto-rfq/store';
import { AutoRFQQueryKey } from 'Services';
import {
  ablySpaceChannelFor,
  ensureResource,
  releaseResource
} from 'lib/realtime/AblyClient';

const EVENT_NAME = 'rfq_item.updated';

interface RfqItemUpdatedPayload {
  itemId: string;
  rfqId: string;
  // `after` may be absent on leaner writes (e.g. catalog-match updates). We
  // don't read it — the item is refetched by id — so it's optional.
  after?: Record<string, any>;
  previous_attributes?: Record<string, any>;
  source: 'user' | 'ingest' | 'quote-sync' | 'pricing-sync' | 'system';
  actor?: { userId: string; name: string };
}

/**
 * Subscribes to per-rfqItem mutation broadcasts. The backend re-broadcasts
 * every user-initiated write to the RFQ channel; self-echo is filtered so the
 * actor's own change doesn't refetch needlessly (their local state already
 * reflects the mutation).
 *
 * Events ride the Spaces channel (`rfq:{id}::$space`) because that's the
 * channel browsers can publish to — the editor broadcasts directly (the
 * backend isn't emitting these). When the payload carries `after` (the
 * editor's post-edit item), it's applied to the store immediately for an
 * instant update; the page-query invalidation that follows is a backstop that
 * pulls any server-side enrichment.
 */
export const useRfqLiveUpdates = (rfqId: string | undefined) => {
  const { user } = useAppContext();
  const queryClient = useQueryClient();
  const markRemoteRefresh = useRFQStore(s => s.markRemoteRefresh);
  const updateProcessedProduct = useRFQStore(s => s.updateProcessedProduct);

  useEffect(() => {
    if (!rfqId) return undefined;
    let cancelled = false;
    let channel: Ably.RealtimeChannel | null = null;
    let listener: ((m: Ably.Message) => void) | null = null;
    let analyticsTimer: ReturnType<typeof setTimeout> | null = null;
    let itemRefetchTimer: ReturnType<typeof setTimeout> | null = null;
    const currentUserId = user?.ID ? String(user.ID) : null;

    // RFQ analytics totals shift whenever an item changes. Refetch them for
    // collaborators, but debounced + delayed so the aggregate read lands after
    // the backend's write is consistent (an immediate read returns a stale
    // count, the same read-after-write race that reverted item rows).
    const scheduleAnalyticsRefresh = () => {
      if (analyticsTimer) clearTimeout(analyticsTimer);
      analyticsTimer = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [AutoRFQQueryKey.GET_RFQ_ANALYTICS]
        });
      }, 1500);
    };

    // Backstop for a live edit on a row this client has NOT loaded yet (absent
    // from both the streaming store AND every cached GET_ITEMS page). The instant
    // apply below (updateProcessedProduct / __rfqLiveOverlay) only touches rows
    // already present, so an unloaded row would silently drop the collaborator's
    // change. Refetch it — but only in the missing-row case, debounced (1500ms,
    // same delay the analytics/read-after-write race uses) and SCOPED to this
    // rfq's items so we don't reintroduce the stale-read that the trust-`after`
    // path deliberately avoids. The existing fetchedItems->store merge (honoring
    // remoteRefreshIds) lands the enriched row once its page loads.
    const scheduleMissingItemRefetch = () => {
      if (itemRefetchTimer) clearTimeout(itemRefetchTimer);
      itemRefetchTimer = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [AutoRFQQueryKey.GET_ITEMS, rfqId]
        });
        queryClient.invalidateQueries({
          queryKey: [AutoRFQQueryKey.GET_ITEMS_METADATA]
        });
      }, 1500);
    };

    (async () => {
      try {
        const ably = await ensureResource({ type: 'rfq', id: rfqId });
        if (cancelled) return;
        channel = ably.channels.get(
          ablySpaceChannelFor({ type: 'rfq', id: rfqId })
        );
        listener = (msg: Ably.Message) => {
          const data = msg?.data as RfqItemUpdatedPayload | undefined;
          // Receipt log — confirms the broadcast actually reaches this client.
          // If this never logs while another user edits, the issue is upstream
          // (backend not publishing / token capability), not the apply path.
          console.info('[rfq-live] rfq_item.updated received', {
            itemId: data?.itemId,
            source: data?.source,
            actor: data?.actor?.userId
          });
          // Only the id is required — the enriched document is refetched, so a
          // payload without `after` (e.g. catalog-match writes) must still pass.
          if (!data?.itemId) return;
          // Self-echo: the actor's own user-driven write comes back over the
          // channel; skip merging because their local store already reflects
          // the optimistic / response-applied state. Non-user sources (e.g.
          // quote-sync back-syncs) still merge so the actor sees the second
          // server-side write.
          if (data.source === 'user' && data.actor?.userId === currentUserId) {
            return;
          }

          // Totals change with any item edit — refresh analytics (debounced).
          scheduleAnalyticsRefresh();

          // Instant apply: the editor ships its post-edit item in `after`,
          // already enriched, so merge it straight into the store and TRUST it.
          // We deliberately do NOT refetch here — an immediate page refetch
          // races the backend's read-after-write and was landing stale data
          // that reverted the row right after it updated. The store overlay
          // keeps this value; the next natural refetch (scroll / reload)
          // returns consistent data and the merge logic preserves it.
          if (data.after && typeof data.after === 'object') {
            updateProcessedProduct(data.itemId, data.after as any);
            // Persist the post-edit item into the page-query cache so the change
            // survives a remount that wipes the transient store — e.g. the
            // Quotes V2 Product Match tab runs cleanup()/reset() on tab switch,
            // which would otherwise drop the live update and re-render the stale
            // cached page (the pre-swap match) on return. Stored as a
            // pre-formatted overlay on the raw item; use-paged-items prefers
            // `__rfqLiveOverlay` over formatProduct(raw). It's cleared naturally
            // on the next page refetch, once the server read is consistent.
            queryClient.setQueriesData(
              { queryKey: [AutoRFQQueryKey.GET_ITEMS, rfqId] },
              (page: any) => {
                if (!page?.data?.some?.((it: any) => it._id === data.itemId)) {
                  return page;
                }
                return {
                  ...page,
                  data: page.data.map((it: any) =>
                    it._id === data.itemId
                      ? { ...it, __rfqLiveOverlay: data.after }
                      : it
                  )
                };
              }
            );

            // If the edited row isn't present anywhere on this client, the two
            // applies above both no-op'd (store map() skips unknown ids; the
            // overlay only rewrites a cached page that already holds the id).
            // Pull it via the debounced, scoped backstop so it surfaces once its
            // page loads. Loaded rows skip this entirely — no extra fetch.
            const inStore = useRFQStore
              .getState()
              .products.some(p => p?.id === data.itemId);
            const inCache = queryClient
              .getQueriesData({
                queryKey: [AutoRFQQueryKey.GET_ITEMS, rfqId]
              })
              .some(([, page]) =>
                (page as any)?.data?.some?.((it: any) => it._id === data.itemId)
              );
            if (!inStore && !inCache) {
              markRemoteRefresh(data.itemId);
              scheduleMissingItemRefetch();
            }
            return;
          }

          // No payload to apply (lean event) — flag the item and refetch so the
          // content layer can swap in the enriched document. Invalidate the
          // metadata backbone too so order / matched-state of not-yet-loaded
          // rows refreshes.
          markRemoteRefresh(data.itemId);
          queryClient.invalidateQueries({
            queryKey: [AutoRFQQueryKey.GET_ITEMS]
          });
          queryClient.invalidateQueries({
            queryKey: [AutoRFQQueryKey.GET_ITEMS_METADATA]
          });
        };
        await channel.subscribe(EVENT_NAME, listener);
      } catch (err) {
        console.warn('useRfqLiveUpdates: subscribe failed', err);
      }
    })();

    return () => {
      cancelled = true;
      if (analyticsTimer) clearTimeout(analyticsTimer);
      if (itemRefetchTimer) clearTimeout(itemRefetchTimer);
      if (channel && listener) {
        try {
          channel.unsubscribe(EVENT_NAME, listener);
        } catch (err) {
          console.warn('useRfqLiveUpdates: unsubscribe failed', err);
        }
      }
      releaseResource({ type: 'rfq', id: rfqId }).catch(err =>
        console.warn('useRfqLiveUpdates: release failed', err)
      );
    };
  }, [rfqId, user?.ID, markRemoteRefresh, updateProcessedProduct, queryClient]);
};
